/**
 * Pestaña Perfil (006 · T050 · US5; contracts/staff-ui.md §5, FR-019/FR-020).
 *
 * Solo lectura: nombre completo, empresa, sedes autorizadas (la principal primero, con "Principal"
 * icono + texto), el teléfono registrado (sistema, navegador y modelo, lo que se conozca; si no, la
 * plataforma) con su fecha de registro, y la versión y fecha de aceptación del aviso de privacidad
 * con enlace a `/aviso-de-privacidad`. Nunca el documento ni ninguna acción de edición: el texto
 * `profile-readonly` dice que los cambios los hace el administrador.
 *
 * Las fechas se formatean en la zona de la **sede principal** (wiring 006 §0.3), nunca con la del
 * teléfono. "Cerrar sesión" abre `LogoutSheet`; confirmar ejecuta `useLogout` (research R-15).
 *
 * Estados: `ProfileLoading` (skeletons; también la usa la puerta de `routes/perfil.tsx`),
 * `OfflineState` sin red y sin datos, `OfflineBanner` sin red con datos en memoria,
 * `GenericErrorScreen` ante un error. Siempre con la barra de pestañas (Perfil activa).
 */
import { Link } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { type StaffProfile, type StaffProfileDevice } from '@fermin/contracts';

import { useOnlineStatus } from '../../api/online';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { GenericErrorScreen } from '../../components/ui/generic-error';
import { OfflineBanner, OfflineState } from '../../components/ui/offline-state';
import { Screen } from '../../components/ui/screen';
import { Skeleton, SkeletonText } from '../../components/ui/skeleton';
import { TabBar } from '../../components/ui/tab-bar';
import { formatDate, t } from '../../i18n';
import { InstallLink } from '../install/install-link';

import { useStaffProfile } from './api';
import { LogoutSheet } from './logout-sheet';
import { useLogout } from './use-logout';

const NAV = <TabBar active="profile" />;

/** Skeletons del Perfil; la puerta de `routes/perfil.tsx` los reutiliza mientras resuelve. */
export function ProfileLoading() {
  return (
    <Screen data-testid="profile-loading" title={t('profile.title')} nav={NAV}>
      <SkeletonText label={t('common.states.loading')} className="gap-fm-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} radius="lg" className="h-20 w-full" />
        ))}
      </SkeletonText>
    </Screen>
  );
}

/** Zona de la sede principal: con ella se formatean las fechas del perfil. */
export function primaryTimezone(profile: Pick<StaffProfile, 'sites'>): string | undefined {
  return (profile.sites.find((site) => site.is_primary) ?? profile.sites[0])?.timezone;
}

function joinDefined(...parts: (string | null)[]): string | null {
  const defined = parts.filter((part): part is string => part !== null && part !== '');
  return defined.length > 0 ? defined.join(' ') : null;
}

/** "Android 14 · Chrome 140 · Pixel 7", con lo que exista; sin sistema, empieza por la plataforma. */
export function describeDevice(device: StaffProfileDevice): string {
  const os = joinDefined(device.os_name, device.os_version);
  const browser = joinDefined(device.browser_name, device.browser_version);
  const parts = [
    os ?? t(`profile.device.platform.${device.platform}`),
    browser,
    device.model,
  ].filter((part): part is string => part !== null && part !== '');
  return parts.join(' · ');
}

function ProfileCard({
  testId,
  title,
  children,
}: {
  testId: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card data-testid={testId}>
      <CardTitle className="text-fm-sm font-semibold text-fm-text-secondary">{title}</CardTitle>
      <CardContent className="mt-fm-2">{children}</CardContent>
    </Card>
  );
}

function ProfileDetails({ profile }: { profile: StaffProfile }) {
  const timeZone = primaryTimezone(profile);
  const acceptedAt = profile.privacy_notice.accepted_at;

  return (
    <div className="flex flex-col gap-fm-4">
      <ProfileCard testId="profile-worker" title={t('profile.worker.title')}>
        <p className="font-display text-fm-lg font-bold text-fm-text">
          {`${profile.worker.first_names} ${profile.worker.last_names}`}
        </p>
      </ProfileCard>

      <ProfileCard testId="profile-company" title={t('profile.company.title')}>
        <p>{profile.company.trade_name}</p>
      </ProfileCard>

      <ProfileCard testId="profile-sites" title={t('profile.sites.title')}>
        <ul className="flex flex-col gap-fm-2">
          {profile.sites.map((site) => (
            <li key={site.id} className="flex min-h-11 flex-wrap items-center gap-fm-2">
              <span>{site.name}</span>
              {site.is_primary ? (
                <span
                  data-testid="profile-site-primary"
                  className="inline-flex items-center gap-fm-1 rounded-fm-pill bg-fm-primary-soft px-fm-2 py-0.5 text-fm-sm font-semibold text-fm-primary-text"
                >
                  <Star aria-hidden="true" size={14} />
                  {t('profile.sites.primary')}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </ProfileCard>

      <ProfileCard testId="profile-device" title={t('profile.device.title')}>
        <p>{describeDevice(profile.device)}</p>
        <p className="mt-fm-1 text-fm-sm text-fm-text-secondary">
          {t('profile.device.enrolledAt', {
            date: formatDate(profile.device.enrolled_at, timeZone),
          })}
        </p>
      </ProfileCard>

      <ProfileCard testId="profile-privacy" title={t('profile.privacy.title')}>
        <p>
          {acceptedAt
            ? t('profile.privacy.accepted', {
                version: profile.privacy_notice.version,
                date: formatDate(acceptedAt, timeZone),
              })
            : t('profile.privacy.version', { version: profile.privacy_notice.version })}
        </p>
        <Link
          to="/aviso-de-privacidad"
          className="mt-fm-2 inline-flex min-h-11 items-center font-ui font-semibold text-fm-primary-compact underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus"
        >
          {t('profile.privacy.read')}
        </Link>
      </ProfileCard>

      <p data-testid="profile-readonly" className="text-fm-sm text-fm-text-secondary">
        {t('profile.readonly')}
      </p>
    </div>
  );
}

export function ProfileScreen() {
  const online = useOnlineStatus();
  const profile = useStaffProfile();
  const { logout, pending } = useLogout();
  const [confirming, setConfirming] = useState(false);

  // Sin red y sin nada cargado todavía en esta sesión: no hay qué mostrar (R-20).
  if (!online && profile.data === undefined) {
    return (
      <Screen title={t('profile.title')} nav={NAV}>
        <OfflineState onRetry={() => void profile.refetch()} retrying={profile.isFetching} />
      </Screen>
    );
  }

  if (profile.isPending) {
    return <ProfileLoading />;
  }

  if (profile.isError) {
    return <GenericErrorScreen nav={NAV} onRetry={() => void profile.refetch()} />;
  }

  return (
    <Screen data-testid="profile-screen" title={t('profile.title')} nav={NAV}>
      {!online ? <OfflineBanner /> : null}

      <ProfileDetails profile={profile.data} />

      {/* Guía animada de instalación; oculta si Fermin ya corre instalada. */}
      <InstallLink />

      {/* Sin red también se puede cerrar sesión: la limpieza local no depende de la API (FR-020). */}
      <Button data-testid="profile-logout" variant="secondary" onClick={() => setConfirming(true)}>
        {t('profile.logout.action')}
      </Button>

      <LogoutSheet
        open={confirming}
        pending={pending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void logout()}
      />
    </Screen>
  );
}
